import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import NavigationBar from '../../components/ui/NavigationBar';
import SearchBar from './components/SearchBar';
import FilterPanel from './components/FilterPanel';
import SchoolMap from './components/SchoolMap';
import SchoolCard from './components/SchoolCard';
import ComparisonPanel from './components/ComparisonPanel';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const SchoolDirectory = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeView, setActiveView] = useState('list'); // 'list' or 'map'
  const [filters, setFilters] = useState({
    region: 'all',
    studyField: 'all',
    institutionType: 'all',
    programLevel: 'all'
  });
  const [selectedSchools, setSelectedSchools] = useState([]);
  const [showComparison, setShowComparison] = useState(false);
  const [favorites, setFavorites] = useState([]);

  // Mock school data for Senegalese institutions
  const mockSchools = [
    {
      id: 1,
      name: "Université Cheikh Anta Diop (UCAD)",
      location: "Dakar",
      region: "Dakar",
      type: "public",
      coordinates: { lat: 14.6937, lng: -17.4441 },
      programs: ["Génie Informatique", "Médecine", "Droit", "Économie", "Lettres"],
      studyFields: ["sciences", "health", "humanities", "business"],
      programLevels: ["licence", "master", "doctorat"],
      studentCount: 85000,
      accreditation: "CAMES",
      rating: 4.5,
      reviewCount: 1250,
      admissionRequirements: "Baccalauréat série S, L ou ES selon la filière",
      tuitionFees: "Gratuit (université publique)",
      contactPhone: "+221 33 824 69 81",
      contactEmail: "info@ucad.edu.sn",
      website: "https://www.ucad.sn",
      description: "La plus grande université du Sénégal, offrant une formation de qualité dans tous les domaines académiques avec des infrastructures modernes.",
      facilities: ["Bibliothèque centrale", "Laboratoires de recherche", "Campus résidentiel", "Centre médical"]
    },
    {
      id: 2,
      name: "Institut Africain de Management (IAM)",
      location: "Dakar",
      region: "Dakar",
      type: "private",
      coordinates: { lat: 14.7167, lng: -17.4677 },
      programs: ["Management", "Finance", "Marketing", "Ressources Humaines"],
      studyFields: ["business"],
      programLevels: ["licence", "master", "mba"],
      studentCount: 3500,
      accreditation: "CAMES, AACSB",
      rating: 4.7,
      reviewCount: 420,
      admissionRequirements: "Baccalauréat + test d'admission + entretien",
      tuitionFees: "2 500 000 - 4 000 000 XOF/an",
      contactPhone: "+221 33 869 05 05",
      contactEmail: "admission@iam.edu.sn",
      website: "https://www.iam.edu.sn",
      description: "École de commerce de référence en Afrique de l'Ouest, reconnue pour l'excellence de ses programmes en management et business.",
      facilities: ["Incubateur d\'entreprises", "Salles informatiques", "Bibliothèque numérique", "Espace coworking"]
    },
    {
      id: 3,
      name: "École Supérieure Polytechnique (ESP)",
      location: "Dakar",
      region: "Dakar",
      type: "public",
      coordinates: { lat: 14.6928, lng: -17.4467 },
      programs: ["Génie Civil", "Génie Électrique", "Génie Informatique", "Génie Mécanique"],
      studyFields: ["sciences"],
      programLevels: ["licence", "master", "ingenieur"],
      studentCount: 4200,
      accreditation: "CAMES, CTI",
      rating: 4.6,
      reviewCount: 680,
      admissionRequirements: "Baccalauréat série S + concours d'entrée",
      tuitionFees: "Gratuit (école publique)",
      contactPhone: "+221 33 825 98 00",
      contactEmail: "contact@esp.sn",
      website: "https://www.esp.sn",
      description: "Grande école d\'ingénieurs formant les futurs cadres techniques du Sénégal avec une approche pratique et innovante.",
      facilities: ["Laboratoires techniques", "Ateliers pratiques", "Centre de recherche", "Fablab"]
    },
    {
      id: 4,
      name: "Université Gaston Berger (UGB)",
      location: "Saint-Louis",
      region: "Saint-Louis",
      type: "public",
      coordinates: { lat: 16.0518, lng: -16.4633 },
      programs: ["Informatique", "Mathématiques", "Physique", "Lettres", "Sciences Économiques"],
      studyFields: ["sciences", "humanities", "business"],
      programLevels: ["licence", "master", "doctorat"],
      studentCount: 18000,
      accreditation: "CAMES",
      rating: 4.3,
      reviewCount: 540,
      admissionRequirements: "Baccalauréat toutes séries selon la filière",
      tuitionFees: "Gratuit (université publique)",
      contactPhone: "+221 33 961 19 06",
      contactEmail: "info@ugb.edu.sn",
      website: "https://www.ugb.sn",
      description: "Université moderne située à Saint-Louis, offrant des formations de qualité dans un cadre académique stimulant.",
      facilities: ["Campus moderne", "Bibliothèque universitaire", "Résidences étudiantes", "Complexe sportif"]
    },
    {
      id: 5,
      name: "Sup de Co Dakar",
      location: "Dakar",
      region: "Dakar",
      type: "private",
      coordinates: { lat: 14.7247, lng: -17.4789 },
      programs: ["Commerce International", "Gestion", "Marketing Digital", "Entrepreneuriat"],
      studyFields: ["business"],
      programLevels: ["licence", "master"],
      studentCount: 2800,
      accreditation: "CAMES",
      rating: 4.4,
      reviewCount: 310,
      admissionRequirements: "Baccalauréat + dossier + entretien",
      tuitionFees: "1 800 000 - 3 200 000 XOF/an",
      contactPhone: "+221 33 869 77 77",
      contactEmail: "contact@supdeco.sn",
      website: "https://www.supdeco.sn",
      description: "École de commerce dynamique formant les managers et entrepreneurs de demain avec une approche internationale.",
      facilities: ["Salles multimédia", "Espace entrepreneuriat", "Bibliothèque", "Salle de conférence"]
    },
    {
      id: 6,
      name: "Institut Supérieur d'Informatique (ISI)",
      location: "Dakar",
      region: "Dakar",
      type: "private",
      coordinates: { lat: 14.7089, lng: -17.4556 },
      programs: ["Développement Web", "Cybersécurité", "Data Science", "Réseaux et Télécoms"],
      studyFields: ["sciences"],
      programLevels: ["licence", "master"],
      studentCount: 1500,
      accreditation: "CAMES",
      rating: 4.5,
      reviewCount: 280,
      admissionRequirements: "Baccalauréat série S + test technique",
      tuitionFees: "1 500 000 - 2 800 000 XOF/an",
      contactPhone: "+221 33 824 54 54",
      contactEmail: "admission@isi.sn",
      website: "https://www.isi.sn",
      description: "Institut spécialisé en informatique offrant des formations pratiques alignées sur les besoins du marché technologique.",
      facilities: ["Laboratoires informatiques", "Salle de serveurs", "Espace projet", "Bibliothèque technique"]
    },
    {
      id: 7,
      name: "Université Alioune Diop de Bambey (UADB)",
      location: "Bambey",
      region: "Diourbel",
      type: "public",
      coordinates: { lat: 14.7019, lng: -16.4567 },
      programs: ["Agronomie", "Sciences de l\'Environnement", "Informatique", "Mathématiques"],
      studyFields: ["sciences", "agriculture"],
      programLevels: ["licence", "master"],
      studentCount: 8500,
      accreditation: "CAMES",
      rating: 4.2,
      reviewCount: 190,
      admissionRequirements: "Baccalauréat série S ou L selon la filière",
      tuitionFees: "Gratuit (université publique)",
      contactPhone: "+221 33 973 41 25",
      contactEmail: "info@uadb.edu.sn",
      website: "https://www.uadb.edu.sn",
      description: "Université spécialisée dans les sciences agricoles et environnementales, contribuant au développement rural du Sénégal.",
      facilities: ["Ferme expérimentale", "Laboratoires", "Bibliothèque", "Résidences"]
    },
    {
      id: 8,
      name: "École Nationale d'Économie Appliquée (ENEA)",
      location: "Dakar",
      region: "Dakar",
      type: "public",
      coordinates: { lat: 14.6847, lng: -17.4389 },
      programs: ["Économie", "Statistique", "Finance", "Planification"],
      studyFields: ["business"],
      programLevels: ["licence", "master"],
      studentCount: 1200,
      accreditation: "CAMES",
      rating: 4.4,
      reviewCount: 150,
      admissionRequirements: "Baccalauréat + concours d'entrée",
      tuitionFees: "Gratuit (école publique)",
      contactPhone: "+221 33 824 12 34",
      contactEmail: "contact@enea.sn",
      website: "https://www.enea.sn",
      description: "École d\'excellence formant les économistes et statisticiens pour les institutions publiques et privées.",
      facilities: ["Salle informatique", "Bibliothèque spécialisée", "Centre de documentation", "Salle de conférence"]
    }
  ];

  // Filter and search logic
  const filteredSchools = useMemo(() => {
    let results = mockSchools;

    // Apply search
    if (searchTerm) {
      results = results?.filter(school =>
        school?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        school?.location?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        school?.programs?.some(p => p?.toLowerCase()?.includes(searchTerm?.toLowerCase()))
      );
    }

    // Apply filters
    if (filters?.region !== 'all') {
      results = results?.filter(school => school?.region === filters?.region);
    }

    if (filters?.studyField !== 'all') {
      results = results?.filter(school => school?.studyFields?.includes(filters?.studyField));
    }

    if (filters?.institutionType !== 'all') {
      results = results?.filter(school => school?.type === filters?.institutionType);
    }

    if (filters?.programLevel !== 'all') {
      results = results?.filter(school => school?.programLevels?.includes(filters?.programLevel));
    }

    return results;
  }, [searchTerm, filters]);

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({ ...prev, [filterName]: value }));
  };

  const handleResetFilters = () => {
    setFilters({
      region: 'all',
      studyField: 'all',
      institutionType: 'all',
      programLevel: 'all'
    });
    setSearchTerm('');
  };

  const handleSchoolSelect = (schoolId, isSelected) => {
    if (isSelected) {
      setSelectedSchools(prev => [...prev, schoolId]);
    } else {
      setSelectedSchools(prev => prev?.filter(id => id !== schoolId));
    }
  };

  const handleToggleFavorite = (schoolId) => {
    if (favorites?.includes(schoolId)) {
      setFavorites(prev => prev?.filter(id => id !== schoolId));
    } else {
      setFavorites(prev => [...prev, schoolId]);
      // Award exploration points
      console.log('Gamification: +5 points for adding school to favorites');
    }
  };

  const handleViewDetails = (school) => {
    console.log('View details for:', school?.name);
    // Award exploration points
    console.log('Gamification: +2 points for viewing school details');
  };

  const selectedSchoolsData = mockSchools?.filter(school => selectedSchools?.includes(school?.id));

  return (
    <>
      <Helmet>
        <title>Annuaire des Écoles - Orientation Sénégal</title>
        <meta name="description" content="Découvrez les établissements d'enseignement supérieur au Sénégal avec notre annuaire complet. Recherchez par domaine, localisation et comparez les programmes." />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
        <NavigationBar />
        <div className="container mx-auto px-4 py-6 md:py-8 max-w-7xl">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2 flex items-center gap-3">
              <Icon name="School" size={36} color="var(--color-primary)" />
              Annuaire des Écoles
            </h1>
            <p className="text-gray-600 text-base md:text-lg">
              Explorez les établissements d'enseignement supérieur au Sénégal et trouvez l'école qui correspond à vos ambitions
            </p>
          </div>

          {/* Search Bar */}
          <SearchBar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            resultCount={filteredSchools?.length}
          />

          {/* Filter Panel */}
          <FilterPanel
            filters={filters}
            onFilterChange={handleFilterChange}
            onResetFilters={handleResetFilters}
            selectedCount={selectedSchools?.length}
            onShowComparison={() => setShowComparison(true)}
          />

          {/* View Toggle */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2 bg-white rounded-lg border border-gray-200 p-1">
              <Button
                variant={activeView === 'list' ? 'default' : 'ghost'}
                size="sm"
                iconName="List"
                onClick={() => setActiveView('list')}
              >
                Liste
              </Button>
              <Button
                variant={activeView === 'map' ? 'default' : 'ghost'}
                size="sm"
                iconName="Map"
                onClick={() => setActiveView('map')}
              >
                Carte
              </Button>
            </div>
            <div className="text-sm text-gray-600">
              <span className="font-semibold">{filteredSchools?.length}</span> établissement(s) trouvé(s)
            </div>
          </div>

          {/* Content Area */}
          {activeView === 'list' ? (
            <div className="grid grid-cols-1 gap-6">
              {filteredSchools?.length > 0 ? (
                filteredSchools?.map(school => (
                  <SchoolCard
                    key={school?.id}
                    school={school}
                    isSelected={selectedSchools?.includes(school?.id)}
                    isFavorite={favorites?.includes(school?.id)}
                    onSelect={handleSchoolSelect}
                    onToggleFavorite={handleToggleFavorite}
                    onViewDetails={handleViewDetails}
                  />
                ))
              ) : (
                <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
                  <Icon name="SearchX" size={64} color="#9CA3AF" className="mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Aucun établissement trouvé</h3>
                  <p className="text-gray-600 mb-4">Essayez de modifier vos critères de recherche ou filtres</p>
                  <Button variant="outline" iconName="RotateCcw" onClick={handleResetFilters}>
                    Réinitialiser les filtres
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <SchoolMap schools={filteredSchools} onSchoolSelect={handleViewDetails} />
          )}
        </div>

        {/* Comparison Panel */}
        {showComparison && selectedSchools?.length >= 2 && (
          <ComparisonPanel
            schools={selectedSchoolsData}
            onClose={() => setShowComparison(false)}
          />
        )}
      </div>
    </>
  );
};

export default SchoolDirectory;